import { cn } from 'lib/utils'
import type { HTMLAttributes } from 'react'

export type StatusTone = 'success' | 'warning' | 'danger' | 'progress' | 'neutral' | 'disabled'

// Dots use the dedicated --status-* marks, NOT the *-foreground half of the
// success/warning/danger pairs: that half means "text on the matching fill" and
// inverts in dark, which rendered every dot a pale near-white. These are vivid
// in light and lifted brighter in dark.
const DOT: Record<StatusTone, string> = {
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  danger: 'bg-status-danger',
  progress: 'bg-status-progress',
  neutral: 'bg-status-neutral',
  disabled: 'bg-status-neutral',
}

// Only danger carries a coloured label — a failure should read as one at a
// glance. --status-danger is tuned to clear 4.5:1 on the page in both themes
// precisely so it can be text as well as a dot.
const TEXT: Record<StatusTone, string> = {
  success: 'text-primary',
  warning: 'text-secondary',
  danger: 'text-status-danger',
  progress: 'text-secondary',
  neutral: 'text-secondary',
  disabled: 'text-tertiary',
}

interface StatusIndicatorProps extends HTMLAttributes<HTMLSpanElement> {
  tone: StatusTone
}

// Kit status treatment for table cells and title bars: a 6px dot plus a
// title-case label, no filled chip. A solid colour block is the only saturated
// fill in an otherwise hairline table and out-shouts the row it sits in; the
// dot carries the state.
//
// Mono, but NOT the `label` token, even though the kit lists status chips among
// that token's uses — the column header directly above these cells IS that
// token, so sharing it made a row's state read as a second header. Title case
// is what separates them. Size steps up to 12px and tracking back to normal:
// `label`'s 0.14em exists to open up all-caps and reads loose on mixed case.
// `capitalize` rather than plain text because several of these labels are raw
// API values ('in progress', 'approved') that were relying on the previous
// `uppercase` to look deliberate; note CSS capitalize only touches the first
// letter of each word, so an ALL-CAPS value must be lowercased at the source.
export const StatusIndicator = ({ tone, className, children, ...props }: StatusIndicatorProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap font-jetbrains text-xs-400 capitalize',
      TEXT[tone],
      className,
    )}
    {...props}
  >
    <span aria-hidden className={cn('size-1.5 shrink-0 rounded-full', DOT[tone])} />
    {children}
  </span>
)

// Bridge for the per-page status mappers that still return legacy Badge
// variant names (`success` / `destructive` / `progress` / …). Lets a call site
// swap a filled chip for the kit indicator without rewriting its mapper.
const TONE_BY_VARIANT: Record<string, StatusTone> = {
  success: 'success',
  warning: 'warning',
  orange: 'warning',
  destructive: 'danger',
  danger: 'danger',
  progress: 'progress',
  blue: 'progress',
  disabled: 'disabled',
  secondary: 'neutral',
  default: 'neutral',
}

export const toneFromBadgeVariant = (variant?: string): StatusTone =>
  (variant && TONE_BY_VARIANT[variant]) || 'neutral'
